import { createContext, useContext } from "react";
import type { PlanGenerationMeta } from "../lib/planGenerationBus";
import type { FuelPlanSummary } from "../lib/preferencesApi";

/**
 * Context handle for the app-root PlanGenerationProvider (split from the
 * provider file so `useGeneratingPlans` can auto-register tracked requests
 * without an import cycle).
 *
 * Dispatch sites call `track()` right after `POST /generate` returns
 * `processing` — the provider then owns polling, the outcome toast, and the
 * cross-tab bus publish, regardless of which screen the user navigates to.
 * Dispatch sites must NOT push their own outcome toasts.
 */
export type TrackOptions = PlanGenerationMeta & {
  /**
   * Show a sticky "building…" pending toast for this request (flipped in
   * place to the outcome when it resolves). Use when the dispatching surface
   * has no inline in-flight UI of its own — e.g. the plan-page regenerate.
   * Journey-rail boxes/kits render their own inline spinner and skip this.
   */
  pendingToast?: string;
};

export type PlanGenerationContextValue = {
  /**
   * Register a just-dispatched request. Idempotent per requestId for a first
   * call, with ONE exception (CT-3): if the id was previously given up on by
   * the foreground poll (`timeout`) but never reached an authoritative terminal,
   * a repeat `track()` (a retry reusing the same still-processing requestId)
   * re-arms the watcher's foreground poll — without re-publishing "started" or
   * pushing a second pending toast.
   */
  track: (requestId: string, options?: TrackOptions) => void;
  /** Request IDs tracked this session that haven't reached a terminal state. */
  inFlight: ReadonlySet<string>;
  /** True when a tracked in-flight request is regenerating `planId`. */
  isPlanRegenerating: (planId: string) => boolean;
  /**
   * The provider's plan-history rows — the ONE plan-history loader per tab
   * (Phase 3). Consumers (JourneyRailContainer) read these
   * instead of mounting their own `useGeneratingPlans`. Completed `plan` rows
   * carry commerce data (`periodTotals`, `coveragePercent`) keyed by
   * `journeyId`/`dropIndex`.
   */
  rows: ReadonlyArray<FuelPlanSummary>;
  /** Force a plan-history reload (e.g. after a journey change). */
  reloadRows: () => void;
  /**
   * Same-tab watcher flash set — request IDs that resolved in the last few
   * seconds. Provenance differs from `usePlanGenerationResolved`'s returned set
   * (which is bus-driven and includes cross-tab); see that hook's doc comment.
   */
  justResolved: ReadonlySet<string>;
  /** True while the provider's single watcher has at least one pending build. */
  hasPending: boolean;
};

/**
 * Default is a no-op so surfaces (and tests) render without the provider;
 * they just lose global toasts, which is the pre-provider behaviour.
 */
const NOOP_VALUE: PlanGenerationContextValue = {
  track: () => {},
  inFlight: new Set<string>(),
  isPlanRegenerating: () => false,
  rows: [],
  reloadRows: () => {},
  justResolved: new Set<string>(),
  hasPending: false
};

export const PlanGenerationContext = createContext<PlanGenerationContextValue>(NOOP_VALUE);

export function usePlanGeneration(): PlanGenerationContextValue {
  return useContext(PlanGenerationContext);
}
